import { AccountAddress, createObjectAddress } from "@aptos-labs/ts-sdk";

import { BaseSDK } from "./base";
import { getMarketAddr } from "./utils";

export class DecibelAdminDex extends BaseSDK {
  /**
   * Initialize the global vault system. Only callable by the admin account.
   */
  async initialize(collateralTokenAddr: string, backstopLiquidatorAddr: string) {
    return await this.sendTx({
      function: `${this.config.deployment.package}::admin_apis::initialize`,
      typeArguments: [],
      functionArguments: [collateralTokenAddr, backstopLiquidatorAddr],
    });
  }

  getProtocolVaultAddress(): AccountAddress {
    const vaultConfigObjectAddr = createObjectAddress(
      AccountAddress.fromString(this.config.deployment.package),
      "GlobalVaultConfig",
    );
    const protocolVaultObjectAddr = createObjectAddress(
      vaultConfigObjectAddr,
      "Decibel Protocol Vault",
    );

    return protocolVaultObjectAddr;
  }

  async initializeProtocolVault(collateralTokenAddr: string, initialFunding: number) {
    return await this.sendTx({
      function: `${this.config.deployment.package}::vault_api::create_and_fund_vault`,
      typeArguments: [],
      functionArguments: [
        this.getPrimarySubaccountAddress(this.account.accountAddress),
        collateralTokenAddr,
        "Decibel Protocol Vault",
        "(description)",
        [],
        "DPV",
        "",
        "",
        0, // fee_bps
        0, // fee_interval
        3 * 24 * 60 * 60, // contribution_lockup_duration_s
        initialFunding, // initial_funding
        true, // accepts_contributions
        false, // delegate_to_creator
      ],
    });
  }

  async delegateProtocolVaultTradingTo(vaultAddress: string, accountToDelegateTo: string) {
    return await this.sendTx({
      function: `${this.config.deployment.package}::vault_admin_api::delegate_dex_actions_to`,
      typeArguments: [],
      functionArguments: [vaultAddress, accountToDelegateTo, undefined],
    });
  }

  async updateVaultUseGlobalRedemptionSlippageAdjustment(
    vaultAddress: string,
    useGlobalRedemptionSlippageAdjustment: boolean,
  ) {
    return await this.sendTx({
      function: `${this.config.deployment.package}::vault_admin_api::update_vault_use_global_redemption_slippage_adjustment`,
      typeArguments: [],
      functionArguments: [vaultAddress, useGlobalRedemptionSlippageAdjustment],
    });
  }

  async authorizeOracleAndMarkUpdate(internalOracleUpdater: string) {
    return await this.sendTx({
      function: `${this.config.deployment.package}::admin_apis::add_oracle_and_mark_update_permission`,
      typeArguments: [],
      functionArguments: [internalOracleUpdater],
    });
  }

  async addAccessControlAdmin(delegatedAdmin: string) {
    return await this.sendTx({
      function: `${this.config.deployment.package}::admin_apis::add_access_control_admin`,
      typeArguments: [],
      functionArguments: [delegatedAdmin],
    });
  }

  async addMarketListAdmin(delegatedAdmin: string) {
    return await this.sendTx({
      function: `${this.config.deployment.package}::admin_apis::add_market_list_admin`,
      typeArguments: [],
      functionArguments: [delegatedAdmin],
    });
  }

  async addMarketRiskGovernor(delegatedAdmin: string) {
    return await this.sendTx({
      function: `${this.config.deployment.package}::admin_apis::add_market_risk_governor`,
      typeArguments: [],
      functionArguments: [delegatedAdmin],
    });
  }

  async registerMarketWithInternalOracle(
    name: string,
    szDecimals: number,
    minSize: number,
    lotSize: number,
    tickerSize: number,
    maxOpenInterest: number,
    maxLeverage: number,
    marginCallFeePct: number,
    taker_in_next_block = true,
    initial_oracle_price = 1,
    max_staleness_secs = 60,
    // Funding multiplier is scaled by 1_000_000 on-chain: 1_000_000 = 1.0x.
    // Range is [0, 10_000_000]. Defaults to 1.0x to preserve existing behavior.
    fundingRateMultiplier = 1_000_000,
    isIsolatedOnly = false,
  ) {
    return await this.sendTx({
      function: `${this.config.deployment.package}::admin_apis::register_market_with_internal_oracle_v3`,
      typeArguments: [],
      functionArguments: [
        name,
        szDecimals,
        minSize,
        lotSize,
        tickerSize,
        maxOpenInterest,
        maxLeverage,
        marginCallFeePct,
        taker_in_next_block,
        isIsolatedOnly,
        fundingRateMultiplier,
        initial_oracle_price,
        max_staleness_secs,
      ],
    });
  }

  async registerMarketWithPythOracle(
    name: string,
    szDecimals: number,
    minSize: number,
    lotSize: number,
    tickerSize: number,
    maxOpenInterest: number,
    maxLeverage: number,
    marginCallFeePct: number,
    taker_in_next_block = true,
    pythIdentifierBytes: number[],
    pythMaxStalenessSecs: number,
    pythConfidenceIntervalThreshold: number,
    pythDecimals: number,
  ) {
    return await this.sendTx({
      function: `${this.config.deployment.package}::admin_apis::register_market_with_pyth_oracle`,
      typeArguments: [],
      functionArguments: [
        name,
        szDecimals,
        minSize,
        lotSize,
        tickerSize,
        maxOpenInterest,
        maxLeverage,
        marginCallFeePct,
        taker_in_next_block,
        pythIdentifierBytes,
        pythMaxStalenessSecs,
        pythConfidenceIntervalThreshold,
        pythDecimals,
      ],
    });
  }

  async registerMarketWithCompositeOraclePrimaryPyth(
    name: string,
    szDecimals: number,
    minSize: number,
    lotSize: number,
    tickerSize: number,
    maxOpenInterest: number,
    maxLeverage: number,
    marginCallFeePct: number,
    taker_in_next_block = true,
    pythIdentifierBytes: number[],
    pythMaxStalenessSecs: number,
    pythConfidenceIntervalThreshold: number,
    pythDecimals: number,
    internalInitialPrice: number,
    internalMaxStalenessSecs: number,
    oraclesDeviationBps: number,
    consecutiveDeviationCount: number,
  ) {
    return await this.sendTx({
      function: `${this.config.deployment.package}::admin_apis::register_market_with_composite_oracle_primary_pyth`,
      typeArguments: [],
      functionArguments: [
        name,
        szDecimals,
        minSize,
        lotSize,
        tickerSize,
        maxOpenInterest,
        maxLeverage,
        marginCallFeePct,
        taker_in_next_block,
        pythIdentifierBytes,
        pythMaxStalenessSecs,
        pythConfidenceIntervalThreshold,
        pythDecimals,
        internalInitialPrice,
        internalMaxStalenessSecs,
        oraclesDeviationBps,
        consecutiveDeviationCount,
      ],
    });
  }

  async registerMarketWithCompositeOraclePrimaryChainlink(
    name: string,
    szDecimals: number,
    minSize: number,
    lotSize: number,
    tickerSize: number,
    maxOpenInterest: number,
    maxLeverage: number,
    marginCallFeePct: number,
    taker_in_next_block = true,
    rescaleDecimals: number,
    chainlinkFeedIdBytes: number[],
    chainlinkMaxStalenessSecs: number,
    internalMaxStalenessSecs: number,
    internalInitialPrice: number,
    oraclesDeviationBps: number,
    consecutiveDeviationCount: number,
    // Funding multiplier is scaled by 1_000_000 on-chain: 1_000_000 = 1.0x.
    // Range is [0, 10_000_000]. Defaults to 1.0x to preserve existing behavior.
    fundingRateMultiplier = 1_000_000,
    isIsolatedOnly = false,
  ) {
    return await this.sendTx({
      function: `${this.config.deployment.package}::admin_apis::register_market_with_composite_oracle_primary_chainlink_v3`,
      typeArguments: [],
      functionArguments: [
        name,
        szDecimals,
        minSize,
        lotSize,
        tickerSize,
        maxOpenInterest,
        maxLeverage,
        marginCallFeePct,
        taker_in_next_block,
        isIsolatedOnly,
        fundingRateMultiplier,
        chainlinkFeedIdBytes,
        chainlinkMaxStalenessSecs,
        rescaleDecimals,
        internalInitialPrice,
        internalMaxStalenessSecs,
        oraclesDeviationBps,
        consecutiveDeviationCount,
      ],
    });
  }

  async registerMarketWithChainlinkBlendedOracle(
    name: string,
    szDecimals: number,
    minSize: number,
    lotSize: number,
    tickerSize: number,
    maxOpenInterest: number,
    maxLeverage: number,
    marginCallFeePct: number,
    takerInNextBlock = true,
    isIsolatedOnly = false,
    sourceNames: string[],
    chainlinkFeedIdBytesArray: number[][],
    internalInitialPrices: number[],
    weights: number[],
    maxStalenessSecs: number,
    chainlinkRescaleDecimals: number,
    oraclesDeviationBps: number,
    consecutiveDeviationCount: number,
    // Funding multiplier is scaled by 1_000_000 on-chain: 1_000_000 = 1.0x.
    // Range is [0, 10_000_000]. Defaults to 1.0x to preserve existing behavior.
    fundingRateMultiplier = 1_000_000,
  ) {
    return await this.sendTx({
      function: `${this.config.deployment.package}::admin_apis::register_market_with_chainlink_blended_oracle_v2`,
      typeArguments: [],
      functionArguments: [
        name,
        szDecimals,
        minSize,
        lotSize,
        tickerSize,
        maxOpenInterest,
        maxLeverage,
        marginCallFeePct,
        takerInNextBlock,
        isIsolatedOnly,
        fundingRateMultiplier,
        sourceNames,
        chainlinkFeedIdBytesArray,
        internalInitialPrices,
        weights,
        maxStalenessSecs,
        chainlinkRescaleDecimals,
        oraclesDeviationBps,
        consecutiveDeviationCount,
      ],
    });
  }

  async updateInternalOraclePrice(marketName: string, oraclePrice: number) {
    const marketAddr = getMarketAddr(marketName, this.config.deployment.perpEngineGlobal);
    return await this.sendTx({
      function: `${this.config.deployment.package}::admin_apis::update_mark_for_internal_oracle`,
      typeArguments: [],
      functionArguments: [marketAddr, oraclePrice, [], [], true],
    });
  }

  async updatePythOraclePrice(marketName: string, vaa: number[]) {
    const marketAddr = getMarketAddr(marketName, this.config.deployment.perpEngineGlobal);
    return await this.sendTx({
      function: `${this.config.deployment.package}::admin_apis::update_mark_for_pyth_oracle`,
      typeArguments: [],
      functionArguments: [marketAddr, vaa, [], [], true],
    });
  }

  async updatePriceToPythOnly(vaas: number[][]) {
    return await this.sendTx({
      function: `${this.config.deployment.package}::pyth::update_price_feeds_with_funder`,
      typeArguments: [],
      functionArguments: [vaas],
    });
  }

  async updatePriceToChainlinkOnly(signedReport: number[]) {
    return await this.sendTx({
      function: `${this.config.deployment.package}::chainlink_state::verify_and_store_single_price`,
      typeArguments: [],
      functionArguments: [signedReport],
    });
  }

  async mintUsdc(toAddr: AccountAddress, amount: number) {
    return await this.sendTx({
      function: `${this.config.deployment.package}::usdc::mint`,
      typeArguments: [],
      functionArguments: [toAddr, amount],
    });
  }

  async setPublicMinting(allow: boolean) {
    return await this.sendTx({
      function: `${this.config.deployment.package}::usdc::set_public_minting`,
      typeArguments: [],
      functionArguments: [allow],
    });
  }

  async setMarketAdlTriggerThreshold(marketName: string, threshold: number) {
    const marketAddr = getMarketAddr(marketName, this.config.deployment.perpEngineGlobal);
    return await this.sendTx({
      function: `${this.config.deployment.package}::admin_apis::set_market_adl_trigger_threshold`,
      typeArguments: [],
      functionArguments: [marketAddr, threshold],
    });
  }

  async setMarketAllowlistOnly(marketName: string, allowlist: string[], reason?: string) {
    const marketAddr = getMarketAddr(marketName, this.config.deployment.perpEngineGlobal);
    return await this.sendTx({
      function: `${this.config.deployment.package}::admin_apis::set_market_allowlist_only`,
      typeArguments: [],
      functionArguments: [marketAddr, allowlist, reason ?? null],
    });
  }

  /** Liquidation slippage increment in chain units (10_000 = 1%). Requires MarketRiskGovernor. */
  async setMarketSlippageIncrementPct(marketName: string, slippageIncrementPct: number) {
    const marketAddr = getMarketAddr(marketName, this.config.deployment.perpEngineGlobal);
    return await this.sendTx({
      function: `${this.config.deployment.package}::admin_apis::set_market_slippage_increment_pct`,
      typeArguments: [],
      functionArguments: [marketAddr, slippageIncrementPct],
    });
  }

  async addMarketModeGuardian(delegatedAdmin: string) {
    return await this.sendTx({
      function: `${this.config.deployment.package}::admin_apis::add_market_mode_guardian`,
      typeArguments: [],
      functionArguments: [delegatedAdmin],
    });
  }

  /**
   * Get the balance of USDC for an account
   * @param addr The account address to get the balance for
   * @returns The balance of USDC for the account
   */
  async usdcBalance(addr: AccountAddress) {
    const balance = await this.aptos.view<[number]>({
      payload: {
        function: `0x1::primary_fungible_store::balance`,
        typeArguments: [`0x1::fungible_asset::Metadata`],
        functionArguments: [addr, this.config.deployment.usdc],
      },
    });
    return balance[0];
  }

  /**
   * Decrease the max open interest for a market (unit-based)
   * @param marketName The market name (e.g., "BTC/USD")
   * @param newOpenInterest The new max open interest in szDecimals units (must be lower than current)
   */
  async decreaseMarketOpenInterest(marketName: string, newOpenInterest: number | bigint) {
    const marketAddr = getMarketAddr(marketName, this.config.deployment.perpEngineGlobal);
    return await this.sendTx({
      function: `${this.config.deployment.package}::admin_apis::decrease_market_open_interest`,
      typeArguments: [],
      functionArguments: [marketAddr.toString(), newOpenInterest.toString()],
    });
  }

  /**
   * Increase the max open interest for a market (unit-based)
   * @param marketName The market name (e.g., "BTC/USD")
   * @param newOpenInterest The new max open interest in szDecimals units (must be higher than current)
   */
  async increaseMarketOpenInterest(marketName: string, newOpenInterest: number | bigint) {
    const marketAddr = getMarketAddr(marketName, this.config.deployment.perpEngineGlobal);
    return await this.sendTx({
      function: `${this.config.deployment.package}::admin_apis::increase_market_open_interest`,
      typeArguments: [],
      functionArguments: [marketAddr.toString(), newOpenInterest.toString()],
    });
  }

  /**
   * Get the max notional open interest for a market
   * @param marketName The market name (e.g., "BTC/USD")
   * @returns The max notional open interest in collateral units (USDC decimals) as a string to preserve precision for large values
   */
  async getMaxNotionalOpenInterest(marketName: string): Promise<string> {
    const marketAddr = getMarketAddr(marketName, this.config.deployment.perpEngineGlobal);
    const result = await this.aptos.view<[string]>({
      payload: {
        function: `${this.config.deployment.package}::perp_engine::get_max_notional_open_interest`,
        typeArguments: [],
        functionArguments: [marketAddr.toString()],
      },
    });
    return result[0];
  }

  /**
   * Decrease the max notional open interest for a market
   * @param marketName The market name (e.g., "BTC/USD")
   * @param newNotionalOpenInterest The new max notional open interest in collateral units (must be lower than current)
   */
  async decreaseMarketNotionalOpenInterest(
    marketName: string,
    newNotionalOpenInterest: number | bigint,
  ) {
    const marketAddr = getMarketAddr(marketName, this.config.deployment.perpEngineGlobal);
    return await this.sendTx({
      function: `${this.config.deployment.package}::admin_apis::decrease_market_notional_open_interest`,
      typeArguments: [],
      functionArguments: [marketAddr.toString(), newNotionalOpenInterest.toString()],
    });
  }

  /**
   * Increase the max notional open interest for a market
   * @param marketName The market name (e.g., "BTC/USD")
   * @param newNotionalOpenInterest The new max notional open interest in collateral units (must be higher than current)
   */
  async increaseMarketNotionalOpenInterest(
    marketName: string,
    newNotionalOpenInterest: number | bigint,
  ) {
    const marketAddr = getMarketAddr(marketName, this.config.deployment.perpEngineGlobal);
    return await this.sendTx({
      function: `${this.config.deployment.package}::admin_apis::increase_market_notional_open_interest`,
      typeArguments: [],
      functionArguments: [marketAddr.toString(), newNotionalOpenInterest.toString()],
    });
  }
}
